/**
 * ToastContext - Provides in-app toast notifications and confirmation modals.
 * Replaces all native browser alert() and confirm() calls.
 */
import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { Toast, ToastItem, ToastType } from './Toast';
import { ConfirmModal } from './ConfirmModal';

interface ToastContextType {
  showToast: (message: string, type?: ToastType) => void;
  confirm: (message: string, title?: string) => Promise<boolean>;
}

const ToastContext = createContext<ToastContextType | null>(null);

export const useToast = (): ToastContextType => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
};

interface ConfirmState {
  message: string;
  title?: string;
  resolve: (value: boolean) => void;
}

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);
  const toastIdCounter = useRef(0);

  const showToast = useCallback((message: string, type: ToastType = 'info') => {
    const id = `toast-${++toastIdCounter.current}-${Date.now()}`;
    const newToast: ToastItem = { id, message, type };
    setToasts((prev) => [...prev, newToast]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const confirmFn = useCallback((message: string, title?: string): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      setConfirmState({ message, title, resolve });
    });
  }, []);

  const handleConfirmResponse = useCallback((value: boolean) => {
    if (confirmState) {
      confirmState.resolve(value);
      setConfirmState(null);
    }
  }, [confirmState]);

  return (
    <ToastContext.Provider value={{ showToast, confirm: confirmFn }}>
      {children}
      {/* Toast stack */}
      <Toast toasts={toasts} onDismiss={removeToast} />
      {/* Confirm modal */}
      <ConfirmModal
        isOpen={!!confirmState}
        message={confirmState?.message || ''}
        title={confirmState?.title}
        onConfirm={() => handleConfirmResponse(true)}
        onCancel={() => handleConfirmResponse(false)}
      />
    </ToastContext.Provider>
  );
};
